import { mixins, rgba, typography } from '../core/utils';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { theme } from '../core/theme';
import { tokens } from '../core/tokens';

const inputBase = css`
   width: 100%;
   padding: ${tokens.space.sm} ${tokens.space.md};
   border: ${tokens.border.thin} ${theme.colors.border.default};
   border-radius: ${tokens.radius.md};
   background-color: ${theme.colors.bg.primary};
   color: ${theme.colors.text.primary};
   font-size: ${tokens.font.size.md};
   transition: border-color ${tokens.transition.fast};
   ${mixins.focusRing};
   ${mixins.disabled};

   &::placeholder {
      color: ${theme.colors.text.disabled};
   }

   &:hover:not(:disabled) {
      border-color: ${theme.colors.border.strong};
   }

   &[aria-invalid='true'] {
      border-color: ${theme.colors.state.error};
   }
`;

export const Input = styled.input<{ hasError?: boolean }>`
   ${inputBase};
   ${({ hasError }) => hasError && `border-color: ${theme.colors.state.error};`}
`;

export const Select = styled.select`
   ${inputBase};
   cursor: pointer;
   &:disabled {
      cursor: not-allowed;
   }
`;

export const NumberInput = styled.input`
   width: 70px;
   padding: ${tokens.space.xs} ${tokens.space.sm};
   border: ${tokens.border.thin} ${theme.colors.border.default};
   border-radius: ${tokens.radius.sm};
   background-color: ${theme.colors.bg.primary};
   color: ${theme.colors.text.primary};
   font-size: ${tokens.font.size.md};
   font-weight: ${tokens.font.weight.normal};
   line-height: ${tokens.font.lineHeight.normal};
   text-align: right;
   transition: ${tokens.transition.fast};
   ${mixins.focusRing}

   &:hover:not(:disabled) {
      border-color: ${theme.colors.border.strong};
   }

   &:disabled {
      background-color: ${theme.colors.bg.tertiary};
      color: ${theme.colors.text.disabled};
   }

   &::-webkit-outer-spin-button,
   &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
   }
   -moz-appearance: textfield;
`;

export const FieldGroup = styled.div`
   display: flex;
   flex-direction: column;
   gap: ${tokens.space.xs};
`;

export const FieldLabel = styled.label`
   ${typography.bodyM}
   color: ${theme.colors.text.secondary};
   font-weight: ${tokens.font.weight.medium};
`;

export const FieldRow = styled.div`
   display: flex;
   align-items: flex-start;
   gap: ${tokens.space.sm};
`;

export const RadioGroup = styled.div`
   display: flex;
   flex-direction: column;
   gap: ${tokens.space.md};
`;

export const RadioOption = styled.div<{ $disabled?: boolean }>`
   display: flex;
   align-items: flex-start;
   opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
   pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

   input[type='radio'],
   input[type='checkbox'] {
      margin-top: ${tokens.space.xs};
      margin-right: ${tokens.space.md};
      cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
      accent-color: ${theme.colors.border.focus};
   }

   label {
      flex: 1;
      cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   }
`;

export const RadioTitle = styled.div`
   ${typography.bodyM}
   font-weight: ${tokens.font.weight.semibold};
   margin-bottom: ${tokens.space.xs};
   color: ${theme.colors.text.primary};
`;

export const RadioDesc = styled.div`
   ${typography.bodyS}
   color: ${theme.colors.text.tertiary};
`;

export const IconButton = styled.button<{
   $variant?: 'default' | 'danger' | 'ghost';
   $size?: 'xs' | 'sm' | 'md';
}>`
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: ${tokens.radius.md};
   cursor: pointer;
   transition: ${tokens.transition.fast};

   ${({ $size = 'md' }) => {
      const sizes = {
         xs: css`
            padding: ${tokens.space.xs};
            width: 28px;
            height: 28px;
            border-radius: ${tokens.radius.sm};
            svg {
               width: 12px;
               height: 12px;
            }
         `,
         sm: css`
            padding: ${tokens.space.xs};
            svg {
               width: 14px;
               height: 14px;
            }
         `,
         md: css`
            padding: ${tokens.space.sm};
            svg {
               width: 20px;
               height: 20px;
            }
         `,
      };
      return sizes[$size];
   }}

   ${({ $variant = 'default' }) => {
      switch ($variant) {
         case 'ghost':
            return css`
               background: none;
               border: none;
               opacity: 0.6;
               color: ${theme.colors.text.secondary};
               &:hover:not(:disabled) {
                  opacity: 1;
               }
            `;
         case 'danger':
            return css`
               border: ${tokens.border.thin} ${theme.colors.border.default};
               background-color: ${theme.colors.bg.primary};
               color: ${theme.colors.state.error};
               &:hover:not(:disabled) {
                  background-color: ${rgba(theme.colors.state.error, 0.1)};
                  border-color: ${theme.colors.state.error};
               }
            `;
         default:
            return css`
               border: ${tokens.border.thin} ${theme.colors.border.default};
               background-color: ${theme.colors.bg.primary};
               color: ${theme.colors.text.secondary};
               &:hover:not(:disabled) {
                  background-color: ${theme.colors.bg.tertiary};
                  border-color: ${theme.colors.border.strong};
               }
            `;
      }
   }}

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

export const ExpandToggle = styled.button`
   display: flex;
   align-items: center;
   gap: ${tokens.space.xs};
   ${typography.bodyM}
   background: none;
   border: none;
   color: ${theme.colors.border.focus};
   font-weight: ${tokens.font.weight.medium};
   cursor: pointer;
   padding: ${tokens.space.sm} 0;
   margin-top: ${tokens.space.sm};
   transition: ${tokens.transition.fast};

   &:hover {
      text-decoration: underline;
   }
`;

export const Button = styled.button<{
   variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
   size?: 'sm' | 'md' | 'lg';
   fullWidth?: boolean;
}>`
   padding: ${tokens.space.sm} ${tokens.space.md};
   border-radius: ${tokens.radius.md};
   font-weight: ${tokens.font.weight.medium};
   cursor: pointer;
   transition: all ${tokens.transition.fast};
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: ${tokens.space.sm};
   ${mixins.disabled};
   ${({ fullWidth }) => fullWidth && 'width: 100%;'}

   ${({ size = 'md' }) => {
      switch (size) {
         case 'sm':
            return css`
               padding: ${tokens.space.xs} ${tokens.space.sm};
               font-size: ${tokens.font.size.sm};
            `;
         case 'lg':
            return css`
               padding: ${tokens.space.md} ${tokens.space.xl};
               font-size: ${tokens.font.size.lg};
            `;
         default:
            return '';
      }
   }}

   ${({ variant = 'primary' }) => {
      switch (variant) {
         case 'primary':
            return css`
               background-color: ${theme.colors.border.focus};
               color: ${theme.colors.bg.primary};
               border: none;
               &:hover:not(:disabled) {
                  background-color: ${rgba(theme.colors.border.focus, 0.85)};
               }
            `;
         case 'secondary':
            return css`
               background-color: ${theme.colors.bg.primary};
               color: ${theme.colors.text.primary};
               border: ${tokens.border.thin} ${theme.colors.border.default};
               &:hover:not(:disabled) {
                  background-color: ${theme.colors.bg.secondary};
               }
            `;
         case 'ghost':
            return css`
               background-color: transparent;
               color: ${theme.colors.border.focus};
               border: none;
               &:hover:not(:disabled) {
                  background-color: ${rgba(theme.colors.border.focus, 0.1)};
               }
            `;
         case 'danger':
            return css`
               background-color: ${theme.colors.state.error};
               color: ${theme.colors.bg.primary};
               border: none;
               &:hover:not(:disabled) {
                  background-color: ${rgba(theme.colors.state.error, 0.85)};
               }
            `;
      }
   }}
`;
